import { AsyncPipe } from '@angular/common'
import { CommonModule } from '@angular/common'
import {
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    signal,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { Router, RouterModule } from '@angular/router'
import {
    AuthStateService,
    UserOrganizationStateService,
} from '@baboor/baboor-auth'
import { SpanishDirective } from '@baboor/baboor-language'
import { SubscriptionStateService } from '@baboor/baboor-subscription'
import { ProfileStateService } from '@baboor/baboor-user'
import {
    UserSettingsApiService,
    UserSettingsStateService,
} from '@baboor/baboor-user-setting'
import { AuthApiService } from '@baboor/common-auth'
import {
    GroupListItemsComponent,
    NavigateButtonComponent,
    UserLoginStatusComponent,
} from '@baboor/common-components'
import { TextSlicePipe } from '@baboor/common-pipes'
import { ThemeService } from '@baboor/common-services'
import { DateUtil } from '@baboor/common-util'
import {
    provideTranslocoScope,
    TranslocoModule,
    TranslocoService,
} from '@jsverse/transloco'
import { WA_LOCATION } from '@ng-web-apis/common'
import { AvatarModule } from '@openng/optimus-ui/avatar'
import { HeaderUtilService } from '../../header-utils/header-util.service'
import { UIstate } from '../../header-utils/ui-state-interface'
import { HeaderQuickSettingsComponent } from '../header-quick-settings/header-quick-settings.component'
import { HeaderOrganizationService } from './header-organization.service'

@Component({
    selector: 'app-header-default',
    imports: [
        RouterModule,
        FormsModule,
        TextSlicePipe,
        UserLoginStatusComponent,
        AsyncPipe,
        NavigateButtonComponent,
        GroupListItemsComponent,
        TranslocoModule,
        SpanishDirective,
        HeaderQuickSettingsComponent,
        AvatarModule,
        CommonModule,
    ],
    templateUrl: './header-default.component.html',
    styleUrl: './header-default.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        provideTranslocoScope('vendorDashboard'),
        HeaderOrganizationService,
    ],
})
export class HeaderDefaultComponent implements OnInit, OnDestroy {
    protected subscriptionStateService = inject(SubscriptionStateService)
    private router = inject(Router)

    protected userGroupStateService = inject(UserOrganizationStateService)
    protected renderer: Renderer2 = inject(Renderer2)
    protected headerUtilService = inject(HeaderUtilService)
    protected authStateService = inject(AuthStateService)
    protected authApiService = inject(AuthApiService)
    protected profileStateService = inject(ProfileStateService)
    private themeService = inject(ThemeService)
    private userSettingsApiService = inject(UserSettingsApiService)
    private readonly location = inject(WA_LOCATION)
    private destroyRef = inject(DestroyRef)
    protected orgService = inject(HeaderOrganizationService)

    protected easyModeDropdownVisible = signal(false)

    protected toggleEasyModeDropdown(): void {
        this.easyModeDropdownVisible.update((value) => !value)
    }

    protected goToEasyMode(): void {
        this.easyModeDropdownVisible.set(false)
        this.router.navigate(['/easy/dashboard/home'])
    }
    private translocoService = inject(TranslocoService)

    groupType = signal<string>('')
    isOrdinaryStaff = signal<boolean>(false)
    @Output() sidebarToggle = new EventEmitter<void>()
    @ViewChild('dropdown') dropdown?: ElementRef
    @ViewChild('orgDropdown') orgDropdown?: ElementRef
    @ViewChild('orgChevron') orgChevron?: ElementRef
    @HostListener('document:click', ['$event'])
    onDocClick(event: MouseEvent) {
        const target = event.target as HTMLElement

        if (!target.closest('.org-dropdown-wrapper')) {
            this.orgService.isOrgDropdownVisible.set(false)
        }
    }

    groups = signal<any>([])
    userId = signal<string>('')

    isVisible = signal<boolean>(false)
    uiState: UIstate = {
        imageLoaded: true,
        showProfileDropDown: false,
    }
    dateUtil = DateUtil

    themeChecked = false

    bodyClickListener: (() => void) | null = null

    toggleSidebar() {
        this.sidebarToggle.emit()
    }

    toggleOrgDropdown() {
        this.orgService.toggleOrgDropdown(this.renderer, this.orgChevron)
    }

    onSwitchGroup(id: string) {
        this.isVisible.set(false)
        this.orgService.onSwitchGroup(id, this.orgChevron)
    }

    showFallback(event: Event) {
        this.headerUtilService.showFallbackText(event, this.uiState)
    }

    ngOnInit() {
        this.bodyClickListener = this.renderer.listen(
            document.body,
            'click',
            (e: Event) => {
                if (
                    this.dropdown?.nativeElement.contains(e.target) === false &&
                    !(e.target as HTMLElement).closest('user-login-status')
                ) {
                    this.isVisible.set(false)
                }
                if (
                    this.orgDropdown?.nativeElement.contains(e.target) ===
                        false &&
                    !(e.target as HTMLElement).closest('.org-name-header')
                ) {
                    this.orgService.isOrgDropdownVisible.set(false)
                    if (this.orgChevron) {
                        this.renderer.removeClass(
                            this.orgChevron.nativeElement,
                            'rotate-90',
                        )
                    }
                }
            },
        )

        this.themeChecked = this.themeService.isDark
        this.profileStateService.init()
        this.groupType.set(this.authStateService.getGroupType() as string)
        this.isOrdinaryStaff.set(this.authStateService.isOrdinaryStaff())
        this.userGroupStateService.init(0)
        this.userGroupStateService
            .select('groups')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((groups) => {
                this.groups.set(groups)
            })
    }

    onThemeChange() {
        this.themeService.setTheme(this.themeChecked)
        const theme = this.themeChecked ? 'dark' : 'light'

        this.userSettingsApiService
            .updateUserSetting(this.userId(), { theme })
            .subscribe()
    }

    logout() {
        this.authStateService.logout()
        this.location.assign('/login')
    }

    onViewDropDown() {
        this.isVisible.set(!this.isVisible())
    }

    onNavigate(string: string) {
        this.router.navigate([string])
        this.isVisible.set(false)
        this.orgService.isOrgDropdownVisible.set(false)
        if (this.orgChevron) {
            this.renderer.removeClass(
                this.orgChevron.nativeElement,
                'rotate-90',
            )
        }
    }

    ngOnDestroy() {
        if (this.bodyClickListener) {
            this.bodyClickListener()
            this.bodyClickListener = null
        }
    }

    onUpgradeSubscription() {
        const isYearly = this.orgService.organization()?.subscriptionIsAnnual
            ? 'true'
            : 'false'
        this.router.navigate(
            [
                '/dashboard/organization/subscribe-plan',
                this.orgService.organization()?.id,
            ],
            {
                queryParams: { isYearly: isYearly },
            },
        )
    }
}
